import { createTheme } from '@mui/material/styles';

export const accessibleColors = {
  page: '#F7F9FA',
  paper: '#FFFFFF',
  text: '#24313D',
  secondaryText: '#52616B',
  primary: '#14645F',
  primaryDark: '#0F4F4B',
  primaryLight: '#EAF4F2',
  visited: '#5B3C88',
  divider: '#CBD5DC',
  inputBorder: '#667580',
  success: '#22623A',
  error: '#A12632',
  warning: '#6E4A00',
  focusInner: '#FFD43B',
  focusOuter: '#24313D',
};

function channel(value) {
  const normalized = value / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

export function contrastRatio(foreground, background) {
  const luminance = color => {
    const hex = color.replace('#', '');
    const values = [0, 2, 4].map(index => parseInt(hex.slice(index, index + 2), 16));
    return (0.2126 * channel(values[0])) + (0.7152 * channel(values[1])) + (0.0722 * channel(values[2]));
  };
  const first = luminance(foreground);
  const second = luminance(background);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

const theme = createTheme({
  palette: {
    mode: 'light',
    background: { default: accessibleColors.page, paper: accessibleColors.paper },
    text: { primary: accessibleColors.text, secondary: accessibleColors.secondaryText },
    primary: {
      main: accessibleColors.primary,
      dark: accessibleColors.primaryDark,
      light: accessibleColors.primaryLight,
      contrastText: accessibleColors.paper,
    },
    divider: accessibleColors.divider,
    success: { main: accessibleColors.success, dark: accessibleColors.success },
    error: { main: accessibleColors.error, dark: accessibleColors.error },
    warning: { main: accessibleColors.warning, dark: accessibleColors.warning },
  },
  typography: {
    fontFamily: [
      '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'sans-serif',
    ].join(','),
    fontSize: 16,
    body1: { fontSize: '1.125rem', lineHeight: 1.6 },
    body2: { fontSize: '0.9375rem', lineHeight: 1.55 },
    button: { fontSize: '1rem', lineHeight: 1.5, textTransform: 'none', fontWeight: 600 },
    h1: { fontSize: 'clamp(2rem, 3.5vw, 2.5rem)', lineHeight: 1.2, fontWeight: 700 },
    h2: { fontSize: 'clamp(1.5rem, 3vw, 1.75rem)', lineHeight: 1.3, fontWeight: 700 },
    h3: { fontSize: 'clamp(1.25rem, 2.5vw, 1.375rem)', lineHeight: 1.4, fontWeight: 700 },
    h4: { fontSize: '1.125rem', lineHeight: 1.45, fontWeight: 700 },
    h5: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 700 },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollPaddingTop: '6rem' },
        body: { overflowWrap: 'break-word' },
        'a:not(.MuiButtonBase-root)': {
          color: accessibleColors.primary,
          textDecoration: 'underline',
          textDecorationThickness: '0.08em',
          textUnderlineOffset: '0.16em',
        },
        'a:not(.MuiButtonBase-root):hover': { color: accessibleColors.primaryDark },
        'a:not(.MuiButtonBase-root):visited': { color: accessibleColors.visited },
        // Outlined fields own their focus border and label notch; an inner
        // input ring would paint over the floating label.
        ':focus-visible:not(.MuiInputBase-input)': {
          outline: `3px solid ${accessibleColors.focusInner}`,
          outlineOffset: '2px',
          boxShadow: `0 0 0 5px ${accessibleColors.focusOuter}`,
        },
        '#main-content:focus': { outline: `3px solid ${accessibleColors.focusInner}` },
        '.skip-link': {
          position: 'fixed', top: '-10rem', left: '1rem', zIndex: 2000,
          padding: '1rem', background: accessibleColors.paper, color: accessibleColors.primary,
        },
        '.skip-link:focus': { top: '0.5rem' },
        '.lab-html': { overflowWrap: 'anywhere' },
        '.lab-html p': { maxWidth: '65ch' },
        '.lab-html img, .lab-html iframe': { maxWidth: '100%' },
        '.lab-html iframe': { border: 0, aspectRatio: '16 / 9', height: 'auto' },
        '.lab-html ul, .lab-html ol': { paddingInlineStart: '1.5rem' },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            scrollBehavior: 'auto !important',
            transitionDuration: '0.01ms !important',
          },
        },
        '@media (forced-colors: active)': {
          ':focus-visible:not(.MuiInputBase-input)': { outline: '3px solid Highlight', boxShadow: 'none' },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'Highlight' },
          'a[aria-current="page"], a[aria-current="location"]': { border: '2px solid currentColor' },
        },
        '@media print': {
          body: { background: '#FFFFFF', color: '#000000' },
          'header, nav, button, .skip-link, .screen-only': { display: 'none !important' },
          main: { padding: 0 },
          input: { color: '#000000 !important', border: '1px solid #000000' },
          '.worksheet-question, figure': { breakInside: 'avoid' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { minHeight: 44, borderRadius: 6 },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { minWidth: 44, minHeight: 44 } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { minWidth: 44, minHeight: 44 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: accessibleColors.paper,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: accessibleColors.inputBorder },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accessibleColors.text },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
        },
        input: { fontSize: '1rem' },
      },
    },
    MuiFormLabel: { styleOverrides: { root: { color: accessibleColors.text } } },
    MuiFormHelperText: { styleOverrides: { root: { fontSize: '0.875rem' } } },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 8,
          '@media (max-width: 480px)': { alignItems: 'stretch', flexDirection: 'column-reverse' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { color: accessibleColors.text, fontWeight: 700, backgroundColor: accessibleColors.primaryLight },
        root: { borderBottomColor: accessibleColors.divider, verticalAlign: 'top' },
      },
    },
    MuiAlert: { styleOverrides: { root: { alignItems: 'flex-start' } } },
    MuiCard: { styleOverrides: { root: { boxShadow: 'none', border: `1px solid ${accessibleColors.divider}` } } },
  },
});

export default theme;
